import { useState } from "react";
import IndexPagination from "@/components/IndexPagination";
import type { DomainIssue, IndexPagination as IndexPaginationData } from "@/types";
import OpenIssueRow from "./OpenIssueRow";

type Props = {
  domainId: string;
  issues: DomainIssue[];
  paging: { pagination: IndexPaginationData; buildPageHref: (page: number) => string };
  staff?: boolean;
  evaluating?: boolean;
  expandedIssueId?: string | null;
  onWatchIssue: (issue: DomainIssue) => void;
};

export default function OpenIssuesList({
  domainId,
  issues,
  paging,
  staff = false,
  evaluating = false,
  expandedIssueId = null,
  onWatchIssue,
}: Props) {
  const { pagination, buildPageHref } = paging;
  const [pagingBusy, setPagingBusy] = useState(false);

  return (
    <section id="open-issues" className="dashboard-section scroll-mt-5 xl:flex-1">
      <div className="dashboard-section-body grid grid-cols-1 items-start md:grid-cols-[minmax(0,1fr)_auto]">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Open issues</h2>
          <p className="text-sm text-gray-500">
            {evaluating ? "Issues appear here as pages finish evaluating" : "Sorted by priority"}
          </p>
        </div>

        <div className="relative mt-6 md:col-span-2">
          <div className={`space-y-3 ${pagingBusy ? "pointer-events-none opacity-50" : ""}`} aria-busy={pagingBusy}>
            {issues.length === 0 ? (
              <p className="rounded-lg border border-dashed border-gray-200 px-4 py-6 text-center text-sm text-gray-500">
                {evaluating
                  ? "No issues yet. Findings will show up as evaluations finish."
                  : "No open issues on this domain right now."}
              </p>
            ) : (
              issues.map((issue) => (
                <OpenIssueRow
                  key={issue.id}
                  domainId={domainId}
                  issue={issue}
                  staff={staff}
                  forceExpanded={expandedIssueId === issue.id}
                  onWatchIssue={onWatchIssue}
                />
              ))
            )}

            {evaluating && issues.length > 0 ? (
              <p className="rounded-lg border border-dashed border-gray-200 px-4 py-4 text-center text-sm text-gray-500">
                More issues may appear as remaining pages finish.
              </p>
            ) : null}
          </div>

          {pagingBusy ? (
            <div className="absolute inset-0 flex items-center justify-center" aria-hidden="true">
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            </div>
          ) : null}
        </div>

        <IndexPagination
          pagination={pagination}
          buildHref={buildPageHref}
          only={["issues", "issues_pagination"]}
          preserveScroll
          preserveState
          hideWhenSinglePage
          scrollToId="open-issues"
          className="mt-6 md:col-start-2 md:row-start-1 md:mt-0"
          busy={pagingBusy}
          onStart={() => setPagingBusy(true)}
          onFinish={() => setPagingBusy(false)}
        />
      </div>
    </section>
  );
}
